import { Box, Stack, Typography, useMediaQuery, useTheme } from "@mui/material";
import { ChangeEvent, FormEvent, useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import CustomDialog, { CustomDialogSlots } from "../components/common/CustomDialog";
import useScreen from "../components/common/UseScreen";
import LoginForm from "../components/login/LoginForm";
import { getSubmitButton } from "../data/custom-dialog-templates";
import { LoginCredentials, loginIntroCards } from "../data/login-page";
import { LoginPageText, SnackbarMsg } from "../data/texts";
import { EventLoppService } from "../services/event-loop.service";
import { LocalStorageKey, LocalStorageService } from "../services/local-storage.service";
import { UserService } from "../services/user.service";
import { showSnackbar, setIsLoading } from "../store/system.store";
import { setUser } from "../store/user.store";
import StyledLoginField from "../styled-cmps/login-field";

export interface Credentials {
  email: string;
  password: string;
}

const LoginPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [isResetPasswordModal, setIsResetPasswordModal] = useState(false);
  const [emailToReset, setEmailToReset] = useState("");
  const [isResetLoading, setIsResetLoading] = useState(false); // New loading state
  const [errorMessage, setErrorMessage] = useState<string | null>(null); // New error state
  const [successMessage, setSuccessMessage] = useState<string | null>(null); // New success message state

  const { isDesktop } = useScreen();
  const theme = useTheme();
  const hideCardsBreakPoint = useMediaQuery(theme.breakpoints.down(1200));

  const handleResetPassword = async () => {
    if (!emailToReset) return;
    try {
      setIsResetLoading(true);
      setErrorMessage(null);
      setSuccessMessage(null);

      await UserService.resetPassword(emailToReset);
      setSuccessMessage("Password reset link sent successfully!");
    } catch (error) {
      console.log("Error in LoginPage: ", error);
      setErrorMessage("Failed to send password reset link.");
    } finally {
      setIsResetLoading(false);
    }
  };

  const handleSubmit = async (ev: FormEvent<HTMLFormElement>, credentials: Credentials) => {
    ev.preventDefault();

    dispatch(setIsLoading(true));
    const user = await UserService.login(credentials);
    console.log("user: ", user);
    if (user) {
      dispatch(setUser(user));
      navigate("/");
      LocalStorageService.set(LocalStorageKey.LOGGED_IN_USER, user);
      EventLoppService.nextTick(() => {
        dispatch(setIsLoading(false));
        dispatch(showSnackbar({ msg: SnackbarMsg.LOGIN }));
      }, 150);
    } else {
      dispatch(setIsLoading(false));
      dispatch(showSnackbar({ msg: SnackbarMsg.WRONG_CREDENTIALS, isError: true }));
    }
  };

  return (
    <Box
      sx={{
        ...theme.customStyles.flexBetween,
        width: "100%",
        height: "100dvh",
      }}
    >
      <CustomDialog
        onClose={() => {
          setErrorMessage(null);
          setSuccessMessage(null);
          setIsResetPasswordModal(false);
        }}
        onButton={handleResetPassword}
        title={LoginPageText.RESET_PASSWORD_TITLE}
        text={LoginPageText.RESET_PASSWORD_INSTRUCTIONS}
        isOpen={isResetPasswordModal}
        isLoading={isResetLoading}
        error={errorMessage}
        successMessage={successMessage}
        buttons={[getSubmitButton()]}
      >
        <slot name={CustomDialogSlots.CONTENT}>
          <StyledLoginField
            onChange={(ev: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setEmailToReset(ev.target.value)}
            label={LoginCredentials.EMAIL.capitalize()}
          />
        </slot>
      </CustomDialog>

      <LoginForm
        handleResetPassword={() => setIsResetPasswordModal(true)}
        containerSx={{
          alignItems: "center",
          width: hideCardsBreakPoint ? (isDesktop ? "80%" : "100%") : "55%",
          marginInline: "auto",
        }}
        handleSubmit={handleSubmit}
      />

      {!hideCardsBreakPoint && (
        <Box
          sx={{
            width: "45%",
            height: "100%",
            background: theme.palette.primary.main,
          }}
        >
          <Stack
            sx={{
              width: "60%",
              margin: "auto",
              justifyContent: "center",
              gap: "30px",
              height: "100%",
            }}
          >
            {loginIntroCards.map((card, idx) => (
              <Stack
                gap="15px"
                sx={{
                  height: `calc(100% / ${loginIntroCards.length} - 40px)`,
                  borderRadius: "10px",
                  padding: "20px",
                  paddingBlock: "30px",
                  background: "#085DB833",
                  color: "white",
                }}
                direction={"row"}
                key={idx}
              >
                <card.icon sx={{ color: "white", fontSize: "50px" }}></card.icon>
                <Stack alignSelf={"center"} gap={"5px"}>
                  <Typography sx={{ fontWeight: "400" }}>{card.title}</Typography>
                  <Typography
                    sx={{
                      fontWeight: "300",
                      fontSize: "15px",
                      ...theme.customStyles.ellipsisFourLines,
                    }}
                  >
                    {card.text}
                  </Typography>
                </Stack>
              </Stack>
            ))}
          </Stack>
        </Box>
      )}
    </Box>
  );
};

export default LoginPage;
